import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@egaranti/components";
import { Button } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { Tag } from "@egaranti/components";
import { Pagination } from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MoreHorizontal, Trash2 } from "lucide-react";

import extendedWarrantyService from "@/services/extendedWarrantyService";

import useMerchantUserType from "@/hooks/useMerchantUserType";
import useSubMerchantTitles from "@/hooks/useSubMerchantTitles";

import useExtendedWarrantyStore from "@/stores/useExtendedWarranty";

import FilterSheet from "@/components/extendedWarranty/transactions/filterSheet";
import SaleStatusTag from "@/components/extendedWarranty/transactions/saleStatusTag";
import CheckRole from "@/components/shared/checkRole";
import EmptyTable from "@/components/shared/EmptyTable";
import PageSizeSelect from "@/components/shared/pageSizeSelect";
import TableHeaderWrapper from "@/components/shared/tableHeaderWrapper";

import ExportIcon from "@/assets/icons/export.svg?react";
import FilterIcon from "@/assets/icons/filter.svg?react";
import PlusIcon from "@/assets/icons/plus.svg?react";

import { isDeletableDraft } from "@/lib/saleStatus";

const ExtendedWarranties = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [openFilterSheet, setOpenFilterSheet] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const { isStoreUser } = useMerchantUserType();
  const { showMerchantColumn, getMerchantTitle } = useSubMerchantTitles();

  const {
    extendedWarranties,
    loading,
    filters,
    getExtendedWarranties,
    getSalesExportQuery,
    setFilters,
  } = useExtendedWarrantyStore();

  const noSalesToExport = () =>
    toast({
      title: t(
        "extendedWarrantiesPage.exportNoData",
        "Export edilecek satış bulunamadı",
      ),
    });

  const onClickExport = async () => {
    const query = getSalesExportQuery();
    if (!query) {
      noSalesToExport();
      return;
    }

    setExportLoading(true);
    try {
      const response = await extendedWarrantyService.exportSales(query);

      if (response.status === 204) {
        noSalesToExport();
        return;
      }
      if (response.status === 202) {
        toast({
          title: t(
            "extendedWarrantiesPage.exportSuccess",
            "Satışlar birkaç dakika içerisinde hesabınıza kayıtlı e-posta adresine gönderilecektir.",
          ),
          variant: "success",
        });
        return;
      }

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "text/csv;charset=utf-8" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ek-hizmet-satislar.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        variant: "error",
        title: t("productsPage.exportError", "Dışa aktarma başarısız"),
      });
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    getExtendedWarranties();
  }, [getExtendedWarranties]);

  const handleFilter = (newFilters) => {
    setFilters({ ...filters, ...newFilters, page: 1 });
    getExtendedWarranties();
  };

  const onPageChange = (page) => {
    setFilters({ ...filters, page });
    getExtendedWarranties();
  };

  const onPageSizeChange = (size) => {
    setFilters({ ...filters, size, page: 1 });
    getExtendedWarranties();
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await extendedWarrantyService.deleteSaleDraft(deleteTarget.id);
      toast({
        title: t("extendedWarrantiesPage.deleteDraftSuccess", "Taslak silindi"),
        variant: "success",
      });
      setDeleteTarget(null);
      await getExtendedWarranties();
    } catch (err) {
      toast({
        title:
          err.response?.data?.errors?.[0]?.message ||
          t("extendedWarrantiesPage.deleteDraftError", "Taslak silinemedi"),
        variant: "error",
      });
      setDeleteTarget(null);
      await getExtendedWarranties();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      <div className="flex items-center justify-end gap-4 pb-4"></div>
      <div className="rounded-lg">
        <div className="flex flex-col rounded-t-lg bg-white pb-4">
          <TableHeaderWrapper className="flex flex-wrap items-center justify-end gap-5">
            <Button
              className="min-w-[150px] font-medium"
              onClick={() => setOpenFilterSheet(true)}
              variant="secondaryGray"
            >
              <FilterIcon />
              <span>{t("common.addFilter")}</span>
            </Button>
            <Button
              className="min-w-[150px] font-medium"
              disabled={exportLoading}
              onClick={onClickExport}
              variant="secondaryGray"
            >
              <ExportIcon />
              <span>{t("common.export")}</span>
            </Button>
            <CheckRole section="ACTIVATIONS_SALES" action="EDIT">
              <Button
                className="min-w-[150px] font-medium"
                onClick={() =>
                  navigate("/extended-warranties/transactions/new")
                }
              >
                <PlusIcon />
                <span>{t("extendedWarrantiesPage.addSale", "Satış Ekle")}</span>
              </Button>
            </CheckRole>
          </TableHeaderWrapper>
          {loading ? (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableSkeleton
                    colSpan={(isStoreUser ? 7 : 8) + (showMerchantColumn ? 1 : 0)}
                  />
                </Table>
              </div>
              <div className="flex flex-col divide-y divide-gray-100 md:hidden">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SatisKartiIskelet key={i} />
                ))}
              </div>
            </>
          ) : extendedWarranties.length ? (
            <>
            {/*
              EPS-5120 (8. bulgu) — liste telefonda masaustu tablosunu aynen
              gosteriyordu. Replikada olculdu (390 px): 7 sutundan 4'u ekranda;
              Plan, Durum ve Gecmis sagda kaliyor, tablo 440 px yana
              kaydirilabiliyor. Bireysel Musteriler'de onaylanan desen uygulandi.
            */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableHead>{t("productsPage.productName")}</TableHead>
                <TableHead>{t("extendedWarrantiesPage.customer")}</TableHead>
                {!isStoreUser && (
                  <TableHead>
                    {t("extendedWarrantiesPage.store", "Bayi")}
                  </TableHead>
                )}
                {/* EPS-5097: bayi kullanıcısı da kendi personelini görsün diye ayrı
                    kolon; "Bayi" kolonu bayi kullanıcısında gizli. */}
                <TableHead>
                  {t(
                    "extendedWarrantiesPage.salesPersonnel",
                    "Satışı Yapan Personel",
                  )}
                </TableHead>
                <TableHead>{t("extendedWarrantiesPage.plan")}</TableHead>
                <TableHead>
                  {t("extendedWarrantiesPage.status.title", "Statü")}
                </TableHead>
                <TableHead>{t("extendedWarrantiesPage.createdDate")}</TableHead>
                {/* EPS-5027: ana bayide liste alt bayilerin satislarini da icerir. */}
                {showMerchantColumn && (
                  <TableHead>{t("subMerchantsPage.title", "Alt Bayi")}</TableHead>
                )}
                <TableHead className="w-12" />
              </TableHeader>
              <TableBody>
                {extendedWarranties?.map((product) => (
                  <TableRow
                    onClick={() =>
                      navigate(
                        // Taslak bir satış değil; detay sayfası yerine sihirbaz
                        // kaldığı yerden açılır.
                        product.isDraft
                          ? `/extended-warranties/transactions/new?draftId=${product.id}`
                          : `/extended-warranties/transactions/${product.id}`,
                      )
                    }
                    role="button"
                    // "Tümü"de iki kaynak birleştiği için id'ler çakışabilir:
                    // taslak id'si seller_sale_draft'tan, satış id'si
                    // extended_warranty'den geliyor.
                    key={product.isDraft ? `draft-${product.id}` : product.id}
                  >
                    {/* EPS-4970 revize: "Ürün Adı" kolonu externalProductName
                        basıyordu — o alan PLANIN adı (extended_warranty.
                        external_product_name), ürünün değil. Plan adı satılan paket
                        olduğu için "Plan" kolonuna taşındı; buraya gerçek ürün adı
                        geliyor. Bilkom gibi ürünü egaranti'de tutulmayan kanallarda
                        productName boş olabilir. */}
                    <TableCell>{product.productName || "-"}</TableCell>
                    <TableCell>
                      {product.firstName} {product.lastName}
                    </TableCell>
                    {!isStoreUser && (
                      <TableCell>
                        {product.storeName || product.storeCode || "-"}
                      </TableCell>
                    )}
                    <TableCell>{product.personalName || "-"}</TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <span>{product.externalProductName || "-"}</span>
                        {/* Süre etiketi: 12 aydan kısa planlarda yıl 0'a yuvarlandığı
                            için ay gösteriliyor. */}
                        {product.extendedWarrantyYear > 0 ? (
                          <Tag size="sm">
                            +{product.extendedWarrantyYear}{" "}
                            {t("extendedWarrantiesPage.year")}
                          </Tag>
                        ) : product.extendedWarrantyMonth > 0 ? (
                          <Tag size="sm">
                            +{product.extendedWarrantyMonth}{" "}
                            {t("extendedWarrantiesPage.month", "ay")}
                          </Tag>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <SaleStatusTag sale={product} />
                    </TableCell>
                    <TableCell>{product.createdAt}</TableCell>
                    {showMerchantColumn && (
                      <TableCell>
                        {getMerchantTitle(product.merchantId)}
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {isDeletableDraft(product) && (
                        <CheckRole section="ACTIVATIONS_SALES" action="EDIT">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-48 bg-white"
                            >
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(product)}
                                className="cursor-pointer text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t("extendedWarrantiesPage.deleteDraft", "Sil")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </CheckRole>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            {/* Mobil kart listesi */}
            <div className="flex flex-col divide-y divide-gray-100 md:hidden">
              {extendedWarranties?.map((product) => (
                <SatisKartiMobil
                  key={product.isDraft ? `draft-${product.id}` : product.id}
                  product={product}
                  isStoreUser={isStoreUser}
                  altBayiBasligi={showMerchantColumn ? getMerchantTitle(product.merchantId) : null}
                  onClick={() =>
                    navigate(
                      product.isDraft
                        ? `/extended-warranties/transactions/new?draftId=${product.id}`
                        : `/extended-warranties/transactions/${product.id}`,
                    )
                  }
                  t={t}
                />
              ))}
            </div>
            </>
          ) : (
            <EmptyTable
              title={t("productsPage.productCouldNotFound")}
              desc={t("productsPage.productCouldNotFoundDesc")}
            />
          )}
          <div className="flex flex-col items-center">
            {filters.totalPages > 1 && (
              <Pagination
                totalPages={filters.totalPages}
                currentPage={filters.page}
                pageSize={filters.size}
                onPageChange={(page) => onPageChange(page)}
              />
            )}
            {!loading && (
              <PageSizeSelect
                onPageSizeChange={onPageSizeChange}
                value={filters.size}
              />
            )}
          </div>
        </div>
      </div>
      <FilterSheet
        open={openFilterSheet}
        onOpenChange={setOpenFilterSheet}
        onFilter={handleFilter}
      />
      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("extendedWarrantiesPage.deleteDraftTitle", "Taslağı Sil")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "extendedWarrantiesPage.deleteDraftWarning",
                "Taslak satış silinecek. Bu işlem geri alınamaz.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t("common.cancel", "Vazgeç")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                onConfirmDelete();
              }}
            >
              {t("extendedWarrantiesPage.deleteDraft", "Sil")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};


/**
 * EPS-5120 (8. bulgu) — Ek Hizmetler listesinin mobil kart gorunumu.
 *
 * Tabloda 7 (ana bayide 8) sutun var, 390 px'de 4'u goruluyordu. Kartta tum
 * alanlar: urun adi (baslik), durum rozeti, musteri, magaza (magaza kullanicisi
 * degilse), personel, plan + sure etiketi, tarih ve varsa alt bayi.
 */
const SatisKartiMobil = ({
  product,
  isStoreUser,
  altBayiBasligi,
  onClick,
  t,
}) => (
  <div
    role="button"
    onClick={onClick}
    className="flex cursor-pointer flex-col gap-2 px-4 py-4 active:bg-[#F9FAFC]"
  >
    <div className="flex items-start justify-between gap-3">
      <span className="min-w-0 flex-1 truncate font-medium text-[#111729]">
        {product.productName || "-"}
      </span>
      <SaleStatusTag sale={product} />
    </div>
    <dl className="space-y-0.5 text-sm text-[#677389]">
      <div className="flex gap-2">
        <dt className="shrink-0">{t("extendedWarrantiesPage.customer")}:</dt>
        <dd className="truncate">
          {product.firstName} {product.lastName}
        </dd>
      </div>
      {!isStoreUser && (
        <div className="flex gap-2">
          <dt className="shrink-0">{t("extendedWarrantiesPage.store", "Mağaza")}:</dt>
          <dd className="truncate">
            {product.storeName || product.storeCode || "-"}
          </dd>
        </div>
      )}
      <div className="flex gap-2">
        <dt className="shrink-0">{t("extendedWarrantiesPage.personal", "Personel")}:</dt>
        <dd className="truncate">{product.personalName || "-"}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="shrink-0">{t("extendedWarrantiesPage.plan")}:</dt>
        <dd className="flex min-w-0 flex-wrap items-center gap-1">
          <span className="truncate">{product.externalProductName || "-"}</span>
          {product.extendedWarrantyYear > 0 ? (
            <Tag size="sm">
              +{product.extendedWarrantyYear} {t("extendedWarrantiesPage.year")}
            </Tag>
          ) : product.extendedWarrantyMonth > 0 ? (
            <Tag size="sm">
              +{product.extendedWarrantyMonth}{" "}
              {t("extendedWarrantiesPage.month", "ay")}
            </Tag>
          ) : null}
        </dd>
      </div>
      <div className="flex gap-2">
        <dt className="shrink-0">{t("extendedWarrantiesPage.createdDate")}:</dt>
        <dd className="truncate">{product.createdAt}</dd>
      </div>
      {altBayiBasligi != null && (
        <div className="flex gap-2">
          <dt className="shrink-0">{t("subMerchantsPage.title", "Alt Bayi")}:</dt>
          <dd className="truncate">{altBayiBasligi}</dd>
        </div>
      )}
    </dl>
  </div>
);

/** Kart yuksekligiyle ayni iskelet. */
const SatisKartiIskelet = () => (
  <div className="flex flex-col gap-2 px-4 py-4">
    <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
    <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
    <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
    <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
    <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
  </div>
);

export default ExtendedWarranties;
